/**
 * Shared change indicator components for entity change cards.
 *
 * - ChangedFieldIndicator: amber dot with tooltip showing previous value
 * - NestedChangeIndicator: indicator for nested array items with sub-field tooltips
 */

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { formatFieldLabel, formatOldValue } from '../utils/formatting';
import type { ChangedFieldsInfo } from '../utils/useChangedFields';

/** Amber dot with tooltip showing previous value */
export const ChangedFieldIndicator: React.FC<{
  oldValue: unknown;
  label: string;
}> = ({ oldValue, label }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span className="inline-flex items-center cursor-help ml-1.5 shrink-0">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
      </span>
    </TooltipTrigger>
    <TooltipContent side="top" className="max-w-xs">
      <p className="text-xs font-normal">
        <span className="text-gray-400">Previous {label}:</span>{' '}
        <span className="text-gray-100">{formatOldValue(oldValue)}</span>
      </p>
    </TooltipContent>
  </Tooltip>
);

/** Change indicator for nested array items — shows sub-field changes in tooltip */
export const NestedChangeIndicator: React.FC<{
  prefix: string;
  changes: ChangedFieldsInfo;
}> = ({ prefix, changes }) => {
  if (!changes.hasChangesUnder(prefix)) return null;

  // Check if entire item is new
  const itemChange = changes.changedPaths.get(prefix);
  if (itemChange && itemChange.oldValue == null) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help ml-1 shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p className="text-xs font-normal">Newly added</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  // Collect sub-field changes (one level deep for readability)
  const subChanges = Array.from(changes.changedPaths.entries())
    .filter(([path]) => path.startsWith(`${prefix}.`))
    .map(([path, change]) => ({
      field: path.slice(prefix.length + 1),
      oldValue: change.oldValue,
    }))
    .filter(({ field }) => !field.includes('.'));

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help ml-1 shrink-0">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-sm">
        {subChanges.length > 0 ? (
          <div className="text-xs font-normal space-y-0.5">
            {subChanges.map(({ field, oldValue }) => (
              <p key={field}>
                <span className="text-gray-400">
                  Previous {formatFieldLabel(field)}:
                </span>{' '}
                <span className="text-gray-100">
                  {formatOldValue(oldValue)}
                </span>
              </p>
            ))}
          </div>
        ) : (
          <p className="text-xs font-normal">Updated</p>
        )}
      </TooltipContent>
    </Tooltip>
  );
};
